/**
 * Full-screen view of the chapter illustration currently showing in
 * `<ChapterScene>` — product ask: children want to look at the details.
 * Night-blue backdrop, image fitted with `object-contain` (never cropped,
 * unlike the small cover-slot version), and a minimal play/pause so the
 * story keeps going while looking. Stays in sync with the same crossfade
 * as `<ChapterScene>` (see `chapterSceneCrossfade.ts`) because it reads
 * the same track/currentTime and runs the exact same `chapterScenes.ts`
 * decision — the two can never disagree on what's "current".
 *
 * Audio is never touched by opening or closing this: it only ever calls
 * `onTogglePlay` on an explicit tap of its own play/pause button, same
 * `toggle()` the page's own button already calls. The `<audio>` element
 * itself lives in `AudioPlayerProvider`, mounted once above `<Routes>`
 * (see `lib/audioPlayer.tsx`'s header comment) — this component neither
 * holds a ref to it nor is it ever an ancestor of it, so mounting or
 * unmounting this view cannot remount, reload, or pause it. Nothing here
 * reads or writes `<audio>` directly.
 *
 * No close button (product ask, second pass): the image itself — really
 * the whole gesture surface, image or backdrop — is the close control. A
 * single tap/click closes it, exactly like tapping the small illustration
 * opened it; a pan/drag or a pinch is never a tap; a genuine double tap
 * still zooms. See `lib/imageZoom.ts` for the tap/drag/pinch
 * classification and the double-tap-window decision this wires up with a
 * real (cancellable) timer.
 *
 * A thin, read-only progress bar sits above play/pause (product ask) —
 * where we are in the story, with a notch at each chapter boundary. It is
 * deliberately not a scrubber, and there's no ±15s here either: a child
 * would use those to flick through the pictures (see lib/storyProgress.ts).
 *
 * iPhone bug report (2026-09-27, PWA/standalone, not reproducible here):
 * pinching back OUT sometimes freezes the app and stops audio, as if the
 * page reloaded. Several defensive changes below target the suspected
 * causes — see each one's own comment, and chapterSceneFullscreenDiagnostics.ts
 * for the evidence-gathering half (Sentry breadcrumbs + a reload detector).
 */
import {
  Component, useEffect, useMemo, useRef, useState, type ComponentProps, type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { Pause, Play } from "lucide-react";
import { Cover } from "./Cover";
import { useChapterSceneLayers, type CrossfadeLayer } from "./chapterSceneCrossfade";
import {
  currentSceneChapter, sceneImageSrc, toSceneChapters,
} from "../lib/chapterScenes";
import {
  classifyPointerGesture, clampScale, DOUBLE_TAP_WINDOW_MS, isDoubleTap, MIN_SCALE, nextDoubleTapScale,
  scaleFromPinch, shouldResetZoomFor,
} from "../lib/imageZoom";
import { addBreadcrumb, captureException } from "../lib/sentry";
import { trackFullscreenLifecycle } from "../lib/chapterSceneFullscreenDiagnostics";
import { chapterMarkerFractions, storyProgressFraction } from "../lib/storyProgress";
import type { AudioTrack } from "../lib/audioPlayer";

function distance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Non-standard Safari-only gesture events — no DOM lib type for these. */
type SafariGestureEventName = "gesturestart" | "gesturechange" | "gestureend";
const SAFARI_GESTURE_EVENTS: SafariGestureEventName[] = ["gesturestart", "gesturechange", "gestureend"];

function ChapterSceneFullscreenView({
  track,
  currentTime,
  duration,
  chapterTitle,
  isPlaying,
  onTogglePlay,
  onClose,
}: {
  track: AudioTrack;
  currentTime: number;
  /** Audio duration in seconds, `0` until known — drives the read-only
   *  progress bar only. */
  duration: number;
  /** Alt text and, for a screen reader, what's actually on screen right
   *  now — the current chapter's title, or the story's own title while
   *  the cover is showing instead. Passed in rather than recomputed here:
   *  the pages already have `currentChapter` from `useAudioPlayer()`. */
  chapterTitle: string;
  isPlaying: boolean;
  onTogglePlay: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // The gesture surface itself is now the close control (no dedicated
  // button) — focused on open, same as the close button used to be.
  const surfaceRef = useRef<HTMLDivElement | null>(null);

  const sceneChapters = useMemo(() => toSceneChapters(track.chapters), [track.chapters]);
  const scene = currentSceneChapter(sceneChapters, currentTime);
  const targetKey = scene ? sceneImageSrc(scene) : null;
  const layers = useChapterSceneLayers(targetKey);

  const [zoom, setZoom] = useState({ scale: 1, x: 0, y: 0 });
  // Whether a gesture (pinch or pan) is actively in progress right now —
  // real state, not just a ref, because `will-change`/transition-duration
  // below must re-render when it flips back to false on pointer-up even
  // when that same event doesn't otherwise change `zoom`. Kept off the
  // compositing layer outside an active gesture on purpose (memory —
  // see the render below).
  const [interacting, setInteracting] = useState(false);
  const prevZoomKeyRef = useRef(targetKey);
  // Reset pan/zoom whenever the picture on screen changes (new chapter,
  // seek, or the cover taking over) — a listener must never end up zoomed
  // into a corner of what is now a different image.
  useEffect(() => {
    if (!shouldResetZoomFor(prevZoomKeyRef.current, targetKey)) return;
    prevZoomKeyRef.current = targetKey;
    setZoom({ scale: 1, x: 0, y: 0 });
  }, [targetKey]);

  // Focus the gesture surface on open (a dialog should never leave focus
  // behind on whatever was under it), no return-focus handling here — the
  // caller (Listen.tsx/KidMode.tsx) owns the trigger button and refocuses
  // it itself once this unmounts, since only it still has that ref.
  useEffect(() => {
    surfaceRef.current?.focus();
  }, []);

  // Escape, and the back gesture/button: pushing a history entry on open
  // means a swipe-back (or Android's back button) fires `popstate` here
  // instead of leaving the page — closing this view rather than /listen
  // itself. Escape and a committed tap-to-close (below) both go through
  // `history.back()` too, so there's exactly one path that actually calls
  // `onClose`. Pinching NEVER calls `history.back()` (see `endPointer`
  // below, which only ever reaches a tap/close decision for a gesture
  // `classifyPointerGesture` calls "tap") — this is deliberate: a
  // history navigation triggered mid-pinch was one suspected cause of the
  // iPhone freeze/reload report, so a pinch must be structurally
  // incapable of reaching this path, not just unlikely to.
  useEffect(() => {
    window.history.pushState({ chapterSceneFullscreen: true }, "");
    function onPopState() {
      addBreadcrumb("chapter fullscreen: popstate", { category: "chapter-scene" });
      onClose();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") window.history.back();
    }
    window.addEventListener("popstate", onPopState);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lock background scroll AND overscroll (pull-to-refresh / the
  // rubber-band bounce at the edges of a scroll) while this covers the
  // screen. Mobile Safari still lets a background scroll through a
  // `position: fixed` overlay via touch on the body without the overflow
  // lock, and `overscroll-behavior` is what stops a pinch/pan that
  // reaches this view's own edges from bleeding into the page's own
  // bounce/refresh gesture underneath — part of the iPhone bug report's
  // defensive fix (an overscroll-triggered pull-to-refresh mid-gesture
  // would itself look exactly like "the page reloaded").
  useEffect(() => {
    const html = document.documentElement;
    const { body } = document;
    const prev = {
      bodyOverflow: body.style.overflow,
      htmlOverscroll: html.style.overscrollBehavior,
      bodyOverscroll: body.style.overscrollBehavior,
    };
    body.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
    body.style.overscrollBehavior = "none";
    return () => {
      body.style.overflow = prev.bodyOverflow;
      html.style.overscrollBehavior = prev.htmlOverscroll;
      body.style.overscrollBehavior = prev.bodyOverscroll;
    };
  }, []);

  // Block Safari's own native pinch-to-zoom gesture while this view is
  // open — scoped here, not app-wide: elsewhere in the app, pinch-zooming
  // the page is still available (product decision keeps this view's own
  // zoom, driven by pointer events below, as the only *replacement* zoom;
  // it does not remove zoom from the rest of the app). `gesturestart`/
  // `gesturechange`/`gestureend` are WebKit-only, non-standard, and fire
  // regardless of `touch-action` — this is the belt to `touch-none`'s
  // braces, since a native page-zoom gesture fighting this view's own
  // pointer-driven one was one suspected cause of the freeze/reload
  // report. `passive: false` so `preventDefault()` actually takes.
  useEffect(() => {
    const target = surfaceRef.current;
    if (!target) return;
    function preventNativeGesture(e: Event) {
      e.preventDefault();
    }
    for (const type of SAFARI_GESTURE_EVENTS) {
      target.addEventListener(type, preventNativeGesture, { passive: false });
    }
    return () => {
      for (const type of SAFARI_GESTURE_EVENTS) {
        target.removeEventListener(type, preventNativeGesture);
      }
    };
  }, []);

  // Evidence-gathering for the bug report above (see
  // chapterSceneFullscreenDiagnostics.ts): marks the view open for the
  // "did the page reload while this was open" detector checked at the
  // next app boot, and adds open/close/pagehide/visibilitychange
  // breadcrumbs either way.
  useEffect(() => trackFullscreenLifecycle(), []);

  // Nothing left with a pending close timer to clean up on unmount (a
  // route change, or the parent unmounting this for some other reason)
  // would otherwise fire `history.back()` after the view is already gone.
  const closeTimeoutRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (closeTimeoutRef.current != null) window.clearTimeout(closeTimeoutRef.current);
  }, []);

  // ── Pinch-to-zoom / pan / tap-to-close — light implementation (product
  // ask): pointer events, scale clamped 1x-3x (lib/imageZoom.ts), double
  // tap toggles 2x, panning unclamped. No library, no pointer capture edge
  // cases beyond what's needed for two simultaneous touches.
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchRef = useRef<{ startDist: number; startScale: number } | null>(null);
  const panRef = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null);
  // The gesture currently in progress (from the first pointer down to the
  // last one up) — `maxPointers` and the start point are what
  // `classifyPointerGesture` needs once it ends. `null` between gestures.
  const gestureRef = useRef<{ startX: number; startY: number; maxPointers: number } | null>(null);
  // Timestamp of the last tap that *could* still become the first half of
  // a double tap — `0` once consumed (by a double tap, or by the close
  // timeout firing) so a third tap doesn't read as double-tapping a stale
  // first one.
  const lastTapAtRef = useRef(0);

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    const wasEmpty = pointersRef.current.size === 0;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (wasEmpty) {
      gestureRef.current = { startX: e.clientX, startY: e.clientY, maxPointers: 1 };
      setInteracting(true);
    } else if (gestureRef.current) {
      gestureRef.current.maxPointers = Math.max(gestureRef.current.maxPointers, pointersRef.current.size);
    }
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      pinchRef.current = { startDist: distance(a, b), startScale: zoom.scale };
      panRef.current = null;
      addBreadcrumb("chapter fullscreen: gesture start", { category: "chapter-scene", data: { scale: zoom.scale } });
    } else if (pointersRef.current.size === 1) {
      panRef.current = {
        startX: e.clientX, startY: e.clientY, originX: zoom.x, originY: zoom.y,
      };
    }
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2 && pinchRef.current) {
      const [a, b] = [...pointersRef.current.values()];
      // Read the pinch start NOW, never inside the setZoom updater: React
      // runs the updater later, during render, and a finger lifted in
      // between has already cleared pinchRef (endPointer). Reading the ref
      // there threw during render, which unmounted the whole app, audio
      // player included (Sentry CHOUETTE-1 and CHOUETTE-2, iPhone, on
      // pinching back out).
      const { startDist, startScale } = pinchRef.current;
      if (!(startDist > 0)) return;
      const ratio = distance(a, b) / startDist;
      setZoom((z) => ({ ...z, scale: scaleFromPinch(startScale, ratio) }));
    } else if (pointersRef.current.size === 1 && panRef.current && zoom.scale > MIN_SCALE) {
      const p = panRef.current;
      setZoom((z) => ({
        ...z, x: p.originX + (e.clientX - p.startX), y: p.originY + (e.clientY - p.startY),
      }));
    }
  }

  /** Closes through the same single path as Escape — see the popstate
   *  effect above. Never called for a pinch or a drag (see `endPointer`). */
  function requestClose() {
    window.history.back();
  }

  /** A gesture that classified as an actual tap: either the second half of
   *  a double tap (zoom, and cancel the pending close from the first tap),
   *  or the first half of one — which schedules a close, cancelled if a
   *  second tap arrives before `DOUBLE_TAP_WINDOW_MS` is up. */
  function handleTap() {
    const now = Date.now();
    if (isDoubleTap(lastTapAtRef.current, now)) {
      lastTapAtRef.current = 0;
      if (closeTimeoutRef.current != null) {
        window.clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setZoom((z) => ({ scale: clampScale(nextDoubleTapScale(z.scale)), x: 0, y: 0 }));
      return;
    }
    lastTapAtRef.current = now;
    closeTimeoutRef.current = window.setTimeout(() => {
      closeTimeoutRef.current = null;
      lastTapAtRef.current = 0;
      requestClose();
    }, DOUBLE_TAP_WINDOW_MS);
  }

  function endPointer(e: React.PointerEvent<HTMLDivElement>) {
    pointersRef.current.delete(e.pointerId);
    const wasPinch = !!pinchRef.current;
    if (pointersRef.current.size < 2) pinchRef.current = null;
    if (wasPinch && !pinchRef.current) {
      addBreadcrumb("chapter fullscreen: gesture end", { category: "chapter-scene", data: { scale: zoom.scale } });
    }
    if (pointersRef.current.size > 0) return;
    panRef.current = null;
    setInteracting(false);
    const gesture = gestureRef.current;
    gestureRef.current = null;
    if (!gesture) return;
    const movedPx = distance({ x: gesture.startX, y: gesture.startY }, { x: e.clientX, y: e.clientY });
    const kind = classifyPointerGesture({ movedPx, maxPointers: gesture.maxPointers });
    // A drag or a pinch is never a tap — no close, no double-tap zoom, and
    // in particular no `history.back()` call anywhere on this path.
    if (kind === "tap") handleTap();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    // Keyboard activation of the "close" control — no double-tap concept
    // for a key press, so this closes immediately rather than waiting.
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      requestClose();
    }
  }

  /** Renders one crossfade layer's content — the chapter image, or the
   *  story cover when this layer represents "no image for this stretch".
   *  Shared by both the resting (full crossfade stack) and zoomed
   *  (single-layer) render paths below, so they never draw it two
   *  different ways. `alt=""`: the dialog's own `aria-label={chapterTitle}`
   *  already announces what this is, and the gesture surface around it
   *  carries the "Fermer l'image" action label — a third, redundant
   *  description on the `<img>` itself would just be noise for a screen
   *  reader. */
  function renderLayerContent(layer: CrossfadeLayer) {
    if (layer.key) {
      return (
        <img
          src={layer.key}
          alt=""
          className="max-w-full max-h-full object-contain"
          style={{ touchAction: "none" }}
          draggable={false}
        />
      );
    }
    return (
      <div className="w-full h-full max-w-sm md:max-w-lg mx-auto flex items-center px-8">
        <Cover
          svg={track.coverSvg}
          imageUrl={track.coverImageUrl}
          imageThumbUrl={track.coverImageThumbUrl}
          imageSize="full"
          title={track.title}
          colorway={track.coverColorway}
          colorKey={track.id}
          rounded="rounded-2xl"
        />
      </div>
    );
  }

  const progress = storyProgressFraction(currentTime, duration);
  const chapterMarkers = useMemo(
    () => chapterMarkerFractions(track.chapters, duration),
    [track.chapters, duration],
  );

  const isZoomed = zoom.scale > MIN_SCALE;
  const topLayer = layers[layers.length - 1];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={chapterTitle}
      className="fixed inset-0 z-50 bg-night flex flex-col safe-top overscroll-none"
      style={{ "--safe-top-extra": "1rem" } as React.CSSProperties}
    >
      {/* The whole gesture surface — image and backdrop alike — is the
       *  close control: `role="button"` + a real aria-label rather than a
       *  visible label, since the visual affordance here is the picture
       *  itself, tappable exactly like the small illustration that opened
       *  it. `touch-none`: this component drives pinch/pan itself, so the
       *  browser's own touch gestures (scroll, native pinch-zoom) must
       *  stay out of the way — reinforced by the scoped `gesturestart`
       *  prevention above for Safari's non-standard gesture events, which
       *  `touch-action` alone does not stop. */}
      <div
        ref={surfaceRef}
        role="button"
        tabIndex={0}
        aria-label={t("player.chapterSceneClose")}
        className="flex-1 relative overflow-hidden touch-none select-none outline-none overscroll-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onKeyDown={onKeyDown}
      >
        {isZoomed ? (
          // Zoomed: freeze the crossfade and mount ONLY the current layer
          // — memory, not just simplicity. Two full-resolution (up to
          // 1024x1536) crossfade layers, both promoted to their own GPU
          // compositing layer and one of them scaled up to 3x, is exactly
          // the kind of allocation that can push a memory-constrained
          // WebContent process over the edge on a real phone (the
          // iPhone bug report this whole block responds to). The
          // transform lives on this single image's own wrapper, not on a
          // container that used to also hold the other (now unmounted)
          // layer — `translate3d` over plain `translate` to make the GPU
          // promotion explicit rather than incidental, and `will-change`
          // only while a gesture is actually live, never left on: a
          // permanent `will-change: transform` on a full-viewport element
          // pins its compositing layer for as long as this view stays
          // open, whether anyone is touching it or not.
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{
              transform: `translate3d(${zoom.x}px, ${zoom.y}px, 0) scale(${zoom.scale})`,
              willChange: interacting ? "transform" : "auto",
            }}
          >
            {renderLayerContent(topLayer)}
          </div>
        ) : (
          // At rest: the normal crossfade stack, exactly as
          // `<ChapterScene>`'s own — no transform, no `will-change`, no
          // compositing-layer promotion at all while nobody is zoomed.
          layers.map((layer, i) => {
            const isTop = i === layers.length - 1;
            return (
              <div
                key={layer.key ?? "__cover__"}
                className={`absolute inset-0 motion-safe:transition-opacity motion-safe:ease-out flex items-center justify-center ${
                  isTop && !layer.entered ? "opacity-0" : "opacity-100"
                }`}
                style={{ transitionDuration: "600ms" }}
              >
                {renderLayerContent(layer)}
              </div>
            );
          })
        )}
      </div>

      <div className="flex flex-col items-center gap-5 pb-[calc(2rem+env(safe-area-inset-bottom,0px))] pt-4 px-8">
        {/* Read-only on purpose — no pointer handlers, no seek: a display,
         *  not a control (see the header comment). Outside the gesture
         *  surface, so tapping it doesn't close the view either. */}
        <div
          role="progressbar"
          aria-label={t("player.storyProgress")}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
          className="relative w-full max-w-sm h-1.5 rounded-full bg-line overflow-hidden"
        >
          <div
            className="h-full bg-sun motion-safe:transition-[width] motion-safe:duration-300 ease-linear"
            style={{ width: `${progress * 100}%` }}
          />
          {chapterMarkers.map((f) => (
            <span
              key={f}
              aria-hidden="true"
              className="absolute inset-y-0 w-0.5 -ml-px bg-night"
              style={{ left: `${f * 100}%` }}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={onTogglePlay}
          // Stops the tap from ever reaching the gesture surface above —
          // this button must control playback, never close the view, no
          // matter how the tap is classified there.
          onPointerDown={(e) => e.stopPropagation()}
          aria-label={isPlaying ? t("player.pause") : t("player.play")}
          className="tap-target w-16 h-16 rounded-full bg-sun text-mint-ink overprint ink-coral flex items-center justify-center"
        >
          {isPlaying ? <Pause size={28} fill="currentColor" /> : <Play size={28} fill="currentColor" className="ml-1" />}
        </button>
      </div>
    </div>
  );
}


/** The full-screen view behind an error boundary: whatever goes wrong in
 *  it (a gesture edge case, a bad image), the view closes and the rest of
 *  the app, the audio player first, keeps running. Without it, an error
 *  during render unmounts the whole React tree. */
export function ChapterSceneFullscreen(props: ComponentProps<typeof ChapterSceneFullscreenView>) {
  return (
    <FullscreenErrorBoundary onError={props.onClose}>
      <ChapterSceneFullscreenView {...props} />
    </FullscreenErrorBoundary>
  );
}

class FullscreenErrorBoundary extends Component<{ onError: () => void; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    captureException(error);
    this.props.onError();
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}
